/**
 * Create-stage entry point for Table and Kanban toolbars.
 * Default path: `useJobPipelineMutations().createStage` (append).
 * Kanban may pass `onCreate` to reuse column-insert position logic.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/atoms/Button';
import { useJobPipelineMutations } from '@/hooks/useJobOffers';
import type { PipelineStage } from '@/models/job';
import {
  applyActionFeedback,
  feedbackForStageMutation,
} from '@/utils/pipeline-action-feedback';
import { CreatePipelineStageDialog } from './CreatePipelineStageDialog';

export interface CreatePipelineStageControlProps {
  jobId: string;
  /**
   * Override persistence (e.g. Kanban `handleColumnAdd` with insert index).
   * When omitted, appends via `createStage`.
   */
  onCreate?: (label: string) => Promise<PipelineStage | void>;
  /** Called after a successful create (e.g. select the new Table tab). */
  onCreated?: (stage: PipelineStage) => void;
  className?: string;
}

export function CreatePipelineStageControl({
  jobId,
  onCreate,
  onCreated,
  className,
}: CreatePipelineStageControlProps) {
  const { t } = useTranslation(['job', 'candidates', 'common']);
  const { createStage } = useJobPipelineMutations(jobId);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [isExternalSubmitting, setIsExternalSubmitting] = useState(false);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setLabel('');
  }, []);

  const handleConfirm = useCallback(
    async (trimmed: string) => {
      try {
        let created: PipelineStage | void | undefined;

        if (onCreate) {
          setIsExternalSubmitting(true);
          try {
            created = await onCreate(trimmed);
          } finally {
            setIsExternalSubmitting(false);
          }
        } else {
          created = await createStage.mutateAsync({ label: trimmed });
          toast.success(
            t('candidates:notifications.stage_create_success', {
              defaultValue: 'Stage created',
              name: trimmed,
            })
          );
        }

        handleOpenChange(false);
        if (created) onCreated?.(created);
      } catch (error) {
        console.error('Error creating pipeline stage:', error);
        if (!onCreate) {
          applyActionFeedback(
            t,
            'candidates:notifications',
            feedbackForStageMutation({
              kind: 'create',
              error: error instanceof Error ? error.message : String(error),
            })
          );
        }
        // When `onCreate` is provided, the caller owns error feedback.
      }
    },
    [createStage, handleOpenChange, onCreate, onCreated, t]
  );

  const createLabel = t('job:pipeline.create_stage', {
    defaultValue: 'Create stage',
  });
  const isSubmitting = onCreate
    ? isExternalSubmitting
    : createStage.isPending;

  return (
    <>
      <Button
        type="contained"
        intent="muted"
        size="md"
        icon={<Plus />}
        aria-label={createLabel}
        title={createLabel}
        className={className}
        onClick={() => setOpen(true)}
      />

      <CreatePipelineStageDialog
        open={open}
        onOpenChange={handleOpenChange}
        label={label}
        onLabelChange={setLabel}
        onConfirm={(next) => {
          void handleConfirm(next);
        }}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
